<template>
  <div class="page-container">
    <div class="page-title-container">
      <h1 class="page-title">
        {{ editing ? "Editar pasajero" : "Nuevo pasajero" }}
      </h1>
    </div>
    <div>
      <form class="custom-form" @submit.prevent="save" novalidate>
        <b-card tag="article" no-body style="max-width: 760px" class="custom-card">
          <b-card-header header-tag="section" class="custom-card-header d-flex align-items-center">
            <h3>
              {{ editing ? "Datos del pasajero" : "Datos del nuevo pasajero" }}
            </h3>
          </b-card-header>

          <b-card-body class="custom-card-body p-0">
            <section class="form-section">
              <div>
                <h2 class="form-subtitle">Información general</h2>
              </div>

              <div class="row">
                <!-- 1. Nombre completo -->
                <div class="col-md-12">
                  <div class="form-group">
                    <label for="full_name">Nombres y apellidos *</label>
                    <input id="full_name" type="text" v-model="form.full_name" autocomplete="off"
                      placeholder="Ingresar nombres y apellidos" class="custom-form-control" :class="{
                        'custom-form-control-error': $v.form.full_name.$error,
                      }" />
                    <FormError v-if="$v.form.full_name.$error && !$v.form.full_name.required"
                      message="Nombres y apellidos son requeridos" />
                  </div>
                </div>

                <!-- 2. Celular | Email -->
                <div class="col-md-6">
                  <div class="form-group">
                    <label for="phone_number">Número de celular *</label>
                    <input id="phone_number" type="text" v-model.trim="form.phone_number" autocomplete="off"
                      placeholder="Ingresar número de celular" class="custom-form-control" :class="{
                        'custom-form-control-error': $v.form.phone_number.$error,
                      }" @input="form.phone_number = $event.target.value.replace(/[^0-9]/g, '')" />
                    <FormError v-if="$v.form.phone_number.$error && !$v.form.phone_number.required"
                      message="Número de celular es requerido" />
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="form-group">
                    <label for="email">Correo electrónico</label>
                    <input id="email" type="email" v-model.trim="form.email" autocomplete="off"
                      placeholder="Ingresar correo electrónico" class="custom-form-control" :class="{
                        'custom-form-control-error': $v.form.email.$error,
                      }" />
                    <FormError v-if="$v.form.email.$error && !$v.form.email.email"
                      message="Correo electrónico no tiene un formato válido" />
                  </div>
                </div>

                <!-- 3. Tipo de documento | Número de documento -->
                <div class="col-md-6">
                  <div class="form-group">
                    <label for="identifier_type">Tipo de documento</label>
                    <FormSelect id="identifier_type" v-model="form.identifier_type" :items="identifierTypes"
                      defaultOption="Elegir tipo de documento" />
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="form-group">
                    <label for="identifier_number">Número de documento</label>
                    <input id="identifier_number" type="text" :maxlength="maxLengthIdentifier"
                      v-model.trim="form.identifier_number" autocomplete="off"
                      placeholder="Ingresar número de documento" class="custom-form-control" :class="{
                        'custom-form-control-error': $v.form.identifier_number.$error,
                      }" />
                    <FormError v-if="$v.form.identifier_number.$error && !$v.form.identifier_number.required"
                      message="Número de documento es requerido si se seleccionó tipo" />
                  </div>
                </div>

                <!-- 4. Tipo de pasajero -->
                <div class="col-md-6">
                  <div class="form-group mb-0">
                    <label for="passenger_type">Tipo de pasajero *</label>
                    <FormSelect id="passenger_type" v-model="form.passenger_type" :items="passengerTypes"
                      defaultOption="Elegir tipo de pasajero" :showError="$v.form.passenger_type.$error &&
                        !$v.form.passenger_type.required" />
                    <FormError v-if="$v.form.passenger_type.$error && !$v.form.passenger_type.required"
                      message="Tipo de pasajero es requerido" />
                  </div>
                </div>
                <!-- Descuentos: ocultos del formulario (DEF-ADM-010); se conservan en BD -->
              </div>

              <div class="row">
                <div class="col-md-12">
                  <div class="form-group-buttons text-right">
                    <router-link :to="{ name: 'config-passenger-list' }" class="button button-light">
                      {{ editing ? "Volver" : "Cancelar" }}
                    </router-link>
                    <button class="button button-primary" type="submit">
                      Guardar
                    </button>
                  </div>
                </div>
              </div>
            </section>
          </b-card-body>
        </b-card>
      </form>
    </div>
  </div>
</template>

<script>
import { Alert, FormSelect, FormError } from "wize-admin";
import { required, requiredIf, email } from "vuelidate/lib/validators";

import { PassengerService, PriceTypeService } from "@/core/services";
import { Constants as LocalConstants } from "@/core/utils";

export default {
  components: {
    FormError,
    FormSelect,
  },
  data() {
    return {
      identifierTypes: [
        LocalConstants.PassengerIdentifierType.DNI,
        LocalConstants.PassengerIdentifierType.CE,
        LocalConstants.PassengerIdentifierType.PASSPORT,
      ],
      passengerTypes: [
        LocalConstants.PassengerType.REGULAR,
        LocalConstants.PassengerType.FRECUENTE,
        LocalConstants.PassengerType.VIP,
      ],
      form: {
        full_name: null,
        phone_number: null,
        email: null,
        identifier_type: null,
        identifier_number: null,
        passenger_type: 1,
        price_type_id: null,
        discount_type: null,
        discount_value: null,
      },
      editing: false,
      // Evita disparar la sugerencia al cargar el pasajero (carga inicial)
      suppressSegmentWatch: false,
    };
  },
  validations() {
    return {
      form: {
        full_name: { required },
        phone_number: { required },
        email: { email },
        identifier_number: {
          required: requiredIf(() => !!this.form.identifier_type),
        },
        passenger_type: { required },
      },
    };
  },
  watch: {
    "$route.params.id": function () {
      if (this.$route.params.id) this.load(this.$route.params.id);
    },
    "form.passenger_type": function (val) {
      if (this.suppressSegmentWatch) return;
      this.onSegmentChange(val);
    },
  },
  computed: {
    maxLengthIdentifier: function () {
      if (this.form.identifier_type) {
        const type = this.identifierTypes.find(
          (i) => i.key === this.form.identifier_type
        );
        return type && type.length ? type.length : 20;
      }
      return 20;
    },
  },
  methods: {
    // Al cambiar el segmento, sugerir la lista de precios (DEF-ADM-010).
    async onSegmentChange(segment) {
      if (!segment) return;
      let sug;
      try {
        const res = await PassengerService.getSuggestedPriceType(this.$route.params.id, segment);
        sug = res.payload;
      } catch (e) {
        console.error(e);
        return;
      }
      if (!sug || sug.action === "none") return;

      if (sug.action === "create") {
        // VIP → proponer crear una lista personalizada
        const ok = await this.$bvModal.msgBoxConfirm(
          "Este segmento requiere una lista de precios personalizada. ¿Deseas crearla ahora?",
          { title: "Crear lista personalizada", okTitle: "Crear", cancelTitle: "Ahora no" }
        );
        if (ok) {
          const nombre = `VIP ${this.form.full_name || ""}`.trim();
          this.$router.push({ name: "tarifas-price-type-create", query: { name: nombre } });
        }
        return;
      }

      // action === "assign"
      const ok = await this.$bvModal.msgBoxConfirm(
        `El segmento cambió. ¿Actualizar la lista de precios a "${sug.suggested_price_type_name}"?`,
        { title: "Sugerencia de lista de precios", okTitle: "Actualizar", cancelTitle: "Mantener" }
      );
      if (!ok) return;

      // Resolver el code a un price_type_id de la ciudad activa (si hay contexto).
      // Los pasajeros son globales; solo se asigna si el operador tiene ciudad activa.
      try {
        const ptRes = await PriceTypeService.list();
        const match = (ptRes.payload || []).find(
          (p) => p.code === sug.suggested_price_type_code && p.is_active
        );
        if (match) {
          this.form.price_type_id = match.id;
          Alert.success(`Lista de precios sugerida: ${sug.suggested_price_type_name}`);
        } else {
          Alert.info(`Sugerencia: ${sug.suggested_price_type_name} (no disponible en la ciudad actual)`);
        }
      } catch (e) {
        console.error(e);
        Alert.info(`Sugerencia: ${sug.suggested_price_type_name}. Selecciónala desde una ciudad para asignarla.`);
      }
    },
    async save() {
      this.$v.$touch();
      if (this.$v.$error) {
        this.$nextTick(() => {
          this.$smoothScroll({
            scrollTo: document.querySelector(".form-error"),
            updateHistory: false,
            offset: -50,
          });
        });
        return;
      }

      try {
        this.$store.dispatch("app/loading", true);

        const data = { ...this.form };

        if (!this.editing) {
          await PassengerService.save(data);
          Alert.success("Se almacenó la información correctamente");
          this.$router.push({ name: "config-passenger-list" });
        } else {
          await PassengerService.update(this.$route.params.id, data);
          Alert.success("Se almacenó la información correctamente");
          this.load(this.$route.params.id);
        }
      } catch (ex) {
        console.error(ex);
        Alert.error(ex.errors.message);
      } finally {
        this.$store.dispatch("app/loading", false);
      }
    },
    async load(id) {
      try {
        this.$store.dispatch("app/loading", true);
        const response = await PassengerService.get(id);
        const payload = response.payload;

        // No disparar la sugerencia al poblar el form desde la BD
        this.suppressSegmentWatch = true;
        this.form = {
          ...payload,
          identifier_type: payload.identifier_type || null,
          discount_type: payload.discount_type || null,
          price_type_id: payload.price_type_id || null,
        };
        this.editing = true;
        this.$nextTick(() => { this.suppressSegmentWatch = false; });
      } catch (e) {
        console.error(e);
        Alert.error(e.errors.message);
        this.$router.push({ name: "config-passenger-list" });
      } finally {
        this.$store.dispatch("app/loading", false);
      }
    },
  },
  async created() {
    if (this.$route.params.id) this.load(this.$route.params.id);
  },
};
</script>

<style lang="stylus" scoped></style>
